import { ExpandLessRounded, ExpandMoreRounded, PsychologyRounded } from '@mui/icons-material';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useState } from 'react';
import Markdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { useThrottledValue } from '@/hooks/use-throttled-value';
import type { ReasoningUIPart } from 'ai';

const markdownComponents = {
    p: ({ children }: { children?: React.ReactNode }) => (
        <>
            {children}
            <br />
        </>
    ),
    a: ({ children, href, ...props }: { children?: React.ReactNode; href?: string }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
        </a>
    ),
};

const TITLE_REGEX = /^\*\*(.*?)\*\*/;

function extractTitle(text: string): string | null {
    const match = TITLE_REGEX.exec(text.trimStart());
    return match ? match[1].trim() : null;
}

const AndieThinkingContent = memo(({ parts }: { parts: ReasoningUIPart[] }) => {
    const { t } = useTranslation();
    const isStreaming = parts.some((p) => p.state === 'streaming');
    const [expanded, setExpanded] = useState(false);

    const extractedTitle = extractTitle(parts[parts.length - 1].text);

    const combinedText = parts.map((p) => p.text).join('\n\n\n\n');
    const throttledText = useThrottledValue(combinedText, 80);

    return (
        <Stack gap={0.5}>
            <ButtonBase
                onClick={() => setExpanded((v) => !v)}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 0.5,
                    alignSelf: 'flex-start',
                    borderRadius: 1,
                    px: 0.5,
                    py: 0.25,
                }}
            >
                <PsychologyRounded
                    fontSize="small"
                    sx={{
                        color: 'text.disabled',
                        ...(isStreaming && {
                            animation: 'thinking-pulse 1.5s ease-in-out infinite',
                            '@keyframes thinking-pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.4 },
                            },
                        }),
                    }}
                />
                <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={`${isStreaming ? 'thinking' : 'thought'}-${extractedTitle ?? ''}`}
                        initial={{ opacity: 0, y: 2 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -2 }}
                        transition={{ duration: 0.2 }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {extractedTitle ??
                                (isStreaming
                                    ? t('andie.chat.thinking.active', { defaultValue: 'Thinking' })
                                    : t('andie.chat.thinking.done', { defaultValue: 'Thought' }))}
                        </Typography>
                    </motion.div>
                </AnimatePresence>
                {expanded ? (
                    <ExpandLessRounded fontSize="small" sx={{ color: 'text.disabled', fontSize: 16 }} />
                ) : (
                    <ExpandMoreRounded fontSize="small" sx={{ color: 'text.disabled', fontSize: 16 }} />
                )}
            </ButtonBase>
            <AnimatePresence initial={false}>
                {expanded && (
                    <motion.div
                        key="thinking-body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        style={{ overflow: 'hidden' }}
                    >
                        <Box
                            sx={{
                                borderLeft: '2px solid',
                                borderColor: 'andie.main',
                                pl: 1.5,
                                maxHeight: 300,
                                overflowY: 'auto',
                            }}
                        >
                            <Typography
                                component="span"
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: 'italic' }}
                            >
                                <Markdown components={markdownComponents}>{throttledText}</Markdown>
                            </Typography>
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>
        </Stack>
    );
});

AndieThinkingContent.displayName = 'AndieThinkingContent';
export default AndieThinkingContent;
